import { HBH_EPISODES, HBH_VIC_ONLY_SLUGS } from './hbhEpisodes';

/**
 * Reverse-linking helper: given a page's title / body / topic hints, find the
 * single most relevant HBH podcast episode to surface as "Watch: ..." on
 * that page.
 *
 * Why reverse-linking matters for SEO:
 *   - Every terminology / learn / lifecycle page now becomes a topical
 *     backlink INTO the /watch/{slug} pages.
 *   - Google reads this as "these deep pages consider this video worth
 *     linking to" which is a stronger signal than the reverse direction.
 *   - Combined with the Clip schema on the episode page, the video result
 *     itself gets a boost from the aggregate internal-link density.
 *
 * Matching strategy:
 *   1. Score each episode by keyword overlap between the page hints and
 *      the episode title + description + tag list.
 *   2. Prefer episodes with matching topic tags (weighted higher).
 *   3. Prefer episodes that already have chapters (better UX + Clip schema).
 *   4. Break ties by recency.
 */
export const findEpisodeForContext = ({ title = '', body = '', topics = [], state = null } = {}) => {
  const blob = `${title} ${body}`.toLowerCase();
  const wantTopics = new Set((topics || []).filter(Boolean));

  const pool = state === 'SA'
    ? HBH_EPISODES.filter((e) => !HBH_VIC_ONLY_SLUGS.has(e.slug))
    : HBH_EPISODES;

  const scores = pool.map((e) => {
    let s = 0;
    const et = (e.title || '').toLowerCase();
    const ed = (e.description || '').toLowerCase();
    // 1. Topic tag overlap (weight 10)
    for (const t of e.topics || []) {
      if (wantTopics.has(t)) s += 10;
    }
    // 2. Title word overlap (weight 3 per matching non-stop word)
    const words = blob
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 3);
    const uniq = new Set(words);
    for (const w of uniq) {
      if (et.includes(w)) s += 3;
      else if (ed.includes(w)) s += 1;
    }
    // 3. Prefer episodes with chapters (better on the destination page)
    if ((e.chapters || []).length > 0) s += 2;
    // 4. Recency tiebreak
    s += (e.number || 0) * 0.01;
    return { e, s };
  });

  scores.sort((a, b) => b.s - a.s);
  return scores[0] && scores[0].s >= 8 ? scores[0].e : null;
};
